import { CommonModule } from '@angular/common'
import { Component, ElementRef, HostListener, NgModule, OnInit } from '@angular/core'
import { Router, RouterModule } from '@angular/router'
import { EmployerNavigationService, ROUTES } from '@employer/app/services/navigation.service'
import { Store } from '@ngrx/store'
import { userActions } from 'shared-lib'

interface MenuItem {
  label: string
  route: string
  icon: string
}

/**
 * Header avatar dropdown that houses the My Account sub-tree per the Story 5.3
 * recommendation (header avatar menu — keeps personal preferences out of the
 * main sidebar rail).
 *
 * Items navigate to their existing `/account/*` routes; Sign Out dispatches the
 * existing `userActions.OnLogOut()` action.
 *
 * Story 5.5. Until the e11-ui-lib user-menu primitive ships (Story 5.2 spike
 * outcome — no existing primitive found), this is composed in-app from a button
 * + popover-style positioned dropdown. Replaceable later with a Tier-A wrapper
 * when ui-lib adds one.
 */
@Component({
  selector: 'app-user-avatar-menu',
  template: `
    <div class="user-avatar-menu e11-relative">
      <button
        type="button"
        class="e11-flex e11-items-center e11-justify-center e11-w-10 e11-h-10 e11-rounded-full e11-bg-skin-primary/10 e11-text-skin-primary hover:e11-bg-skin-primary/20 focus:e11-outline-none focus:e11-ring-2 focus:e11-ring-skin-primary"
        [attr.aria-label]="open ? 'Close account menu' : 'Open account menu'"
        [attr.aria-expanded]="open"
        aria-haspopup="menu"
        (click)="toggle($event)"
      >
        <span class="material-symbols-outlined">person</span>
      </button>
      <div
        *ngIf="open"
        role="menu"
        class="user-avatar-menu__panel e11-absolute e11-right-0 e11-top-12 e11-min-w-[240px] e11-bg-skin-white e11-shadow-xl e11-rounded-md e11-border e11-border-skin-grey/20 e11-z-50"
      >
        <ul class="e11-py-2">
          <li *ngFor="let item of items">
            <a
              role="menuitem"
              [routerLink]="item.route"
              class="e11-flex e11-items-center e11-gap-3 e11-px-4 e11-py-2 hover:e11-bg-skin-primary/5 e11-cursor-pointer"
              (click)="close()"
            >
              <span class="material-symbols-outlined e11-text-skin-grey">{{ item.icon }}</span>
              <span>{{ item.label }}</span>
            </a>
          </li>
          <li class="e11-border-t e11-border-skin-grey/20 e11-mt-2 e11-pt-2">
            <button
              role="menuitem"
              type="button"
              class="e11-w-full e11-flex e11-items-center e11-gap-3 e11-px-4 e11-py-2 hover:e11-bg-skin-primary/5 e11-text-left"
              (click)="signOut()"
            >
              <span class="material-symbols-outlined e11-text-skin-grey">logout</span>
              <span>Sign out</span>
            </button>
          </li>
        </ul>
      </div>
    </div>
  `,
})
export class UserAvatarMenuComponent implements OnInit {
  open = false

  items: MenuItem[] = [
    { label: 'Personal Information', route: ROUTES.USER_PERSONAL, icon: 'person' },
    { label: 'Decision Team Settings', route: ROUTES.HIRING_TEAM_SETTINGS, icon: 'group' },
    { label: 'Profile Picture', route: ROUTES.USER_PIC, icon: 'photo_camera' },
    { label: 'Profile Video', route: ROUTES.USER_VIDEO, icon: 'videocam' },
    { label: 'Security', route: ROUTES.USER_SECURITY, icon: 'lock' },
    { label: 'Notifications', route: ROUTES.USER_NOTIFICATIONS, icon: 'notifications' },
  ]

  constructor(private elementRef: ElementRef<HTMLElement>, private router: Router, private store: Store, private nav: EmployerNavigationService) {}

  ngOnInit(): void {
    // No-op — keeps the menu items computed at construction.
  }

  toggle(ev: MouseEvent): void {
    ev.stopPropagation()
    this.open = !this.open
  }

  close(): void {
    this.open = false
  }

  signOut(): void {
    this.close()
    this.store.dispatch(userActions.OnLogOut())
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.open) return
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.close()
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open) this.close()
  }
}

@NgModule({
  declarations: [UserAvatarMenuComponent],
  imports: [CommonModule, RouterModule],
  exports: [UserAvatarMenuComponent],
})
export class UserAvatarMenuModule {}
